import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Settings } from "lucide-react";
import {
  ANALYTICS_PREFERENCES_EVENT,
  applyAnalyticsConsent,
  getAnalyticsConsent,
  setAnalyticsConsent,
} from "@/lib/analyticsConsent";

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [optionalAnalytics, setOptionalAnalytics] = useState(false);

  useEffect(() => {
    const current = getAnalyticsConsent();
    setVisible(current === null);
    setOptionalAnalytics(current === "accepted");
  }, []);

  useEffect(() => {
    const handler = () => {
      setOptionalAnalytics(getAnalyticsConsent() === "accepted");
      setPrefsOpen(true);
    };
    window.addEventListener(ANALYTICS_PREFERENCES_EVENT, handler);
    return () => window.removeEventListener(ANALYTICS_PREFERENCES_EVENT, handler);
  }, []);

  const handleChoice = (value: "accepted" | "declined") => {
    setAnalyticsConsent(value);
    applyAnalyticsConsent(value);
    setOptionalAnalytics(value === "accepted");
    setVisible(false);
  };

  const handleSavePreferences = () => {
    const value = optionalAnalytics ? "accepted" : "declined";
    setAnalyticsConsent(value);
    applyAnalyticsConsent(value);
    setPrefsOpen(false);
    setVisible(false);
  };

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 px-4 pb-4">
      {visible && (
        <div className="mx-auto max-w-4xl rounded-xl border border-border/70 bg-background/95 p-4 shadow-lg backdrop-blur">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">We use cookies.</span>{" "}
              Analytics cookies help us understand product usage and improve the Portal. You can opt out anytime.
              <Link className="ml-2 text-primary underline hover:no-underline" to="/privacy">
                Learn more about our Privacy Policy
              </Link>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
              <Button variant="outline" size="sm" onClick={() => handleChoice("declined")}>
                Decline analytics
              </Button>
              <Button size="sm" onClick={() => handleChoice("accepted")}>
                Accept analytics
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPrefsOpen(true)}
                aria-label="Manage cookie preferences"
              >
                <Settings className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
      <Dialog open={prefsOpen} onOpenChange={setPrefsOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Cookie preferences</DialogTitle>
            <DialogDescription>
              Manage your cookie preferences. Analytics are optional and only enabled if you accept.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {/* Essential - Always on */}
            <div className="rounded-lg border border-border/60 bg-muted/20 px-4 py-3 text-sm">
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-semibold text-foreground">Essential functionality</span>
                  <span className="ml-2 text-xs text-muted-foreground">(Required)</span>
                </div>
                <Switch checked disabled aria-label="Essential functionality always enabled" />
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                Needed for core site features and security checks. No optional analytics cookies.
              </p>
            </div>
            {/* Optional - PostHog */}
            <div className="rounded-lg border border-border/60 bg-muted/20 px-4 py-3 text-sm">
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-semibold text-foreground">Analytics</span>
                  <span className="ml-2 text-xs text-muted-foreground">(Optional)</span>
                </div>
                <Switch
                  checked={optionalAnalytics}
                  onCheckedChange={setOptionalAnalytics}
                  aria-label="Toggle analytics"
                />
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                Enables privacy-focused analytics and product insights to improve the Portal experience.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button size="sm" onClick={handleSavePreferences}>
              Save preferences
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
